import { MEDALS, MedalDef, MedalLayers, MedalShape, Metal, medalShape } from "../shared/achievements";
import { iconPath } from "./icons";
import { reducedMotion } from "./motion_pref";

// a medal drawn in layers: a cut metal face with its emblem, three canvases of dirt on top (haze under grime under rust), and the holographic
// foil that only a fully polished one gets. the layers come from the one dirt number the server keeps (layersOfDirt)

export function medalDef(id: string): MedalDef | undefined {
  return MEDALS.find((medal) => medal.id === id);
}

// a medal that has a secret flag keeps its name back until it is earned. the flag is optional data on the definition
export const isSecret = (def: MedalDef): boolean => Boolean(def.secret);

export function calm(): boolean {
  return reducedMotion();
}

// ---------- shapes, on a 100 grid ----------

function poly(n: number, outer: number, inner: number, rotate: number): string {
  const steps = inner ? n * 2 : n;
  const points: string[] = [];
  for (let i = 0; i < steps; i++) {
    const radius = inner && i % 2 ? inner : outer;
    const angle = (rotate * Math.PI) / 180 + (i * 2 * Math.PI) / steps;
    points.push(`${(50 + radius * Math.cos(angle)).toFixed(1)} ${(50 + radius * Math.sin(angle)).toFixed(1)}`);
  }
  return `M${points.join("L")}Z`;
}

function wave(lobes: number, base: number, amp: number): string {
  const points: string[] = [];
  for (let i = 0; i < 160; i++) {
    const angle = (i / 160) * Math.PI * 2;
    const radius = base + amp * Math.sin(angle * lobes);
    points.push(`${(50 + radius * Math.cos(angle)).toFixed(1)} ${(50 + radius * Math.sin(angle)).toFixed(1)}`);
  }
  return `M${points.join("L")}Z`;
}

function gear(): string {
  const points: string[] = [];
  const teeth = 12;
  for (let i = 0; i < teeth * 4; i++) {
    const seg = i % 4;
    const radius = seg === 1 || seg === 2 ? 47 : 38;
    const angle = -Math.PI / 2 + ((i - (seg === 2 ? 0.2 : seg === 1 ? -0.2 : 0)) * Math.PI * 2) / (teeth * 4);
    points.push(`${(50 + radius * Math.cos(angle)).toFixed(1)} ${(50 + radius * Math.sin(angle)).toFixed(1)}`);
  }
  return `M${points.join("L")}Z`;
}

export const SHAPES: Record<MedalShape, string> = {
  circle: "M50 4A46 46 0 1 1 49.9 4Z",
  hex: poly(6, 47, 0, -90),
  shield: "M16 8H84V50C84 74 66 88 50 96C34 88 16 74 16 50Z",
  star: poly(5, 48, 21, -90),
  octa: "M32 4H68L96 32V68L68 96H32L4 68V32Z",
  diamond: "M50 3L95 50L50 97L5 50Z",
  rosette: wave(14, 43, 4),
  gear: gear()
};

const maskCache = new Map<MedalShape, string>();
function maskUrl(shape: MedalShape): string {
  let url = maskCache.get(shape);
  if (!url) {
    url = `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path d="${SHAPES[shape]}" fill="#000"/></svg>`)}")`;
    maskCache.set(shape, url);
  }
  return url;
}

// ---------- metals ----------

export const METALS: Record<Metal, { bg: string; rim: string; ink: string }> = {
  bronze: { bg: "linear-gradient(145deg,#f3b985,#b0692f 45%,#6b3a16)", rim: "#ffd7a8", ink: "rgba(60,28,8,0.8)" },
  silver: { bg: "linear-gradient(145deg,#ffffff,#b2bccb 45%,#69768a)", rim: "#ffffff", ink: "rgba(30,38,54,0.78)" },
  gold: { bg: "linear-gradient(145deg,#fff2b0,#e3ac1c 45%,#8a5d06)", rim: "#fff3b8", ink: "rgba(70,40,0,0.8)" },
  crystal: { bg: "conic-gradient(from 200deg,#c6f6ff,#a99bff,#ffd1f3,#9ff0ff,#c6f6ff)", rim: "#ffffff", ink: "rgba(40,30,90,0.7)" }
};

// ---------- the dirt, drawn from a seed so a medal looks the same every time ----------

function rng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 4294967296;
  };
}

export function seedOf(id: string): number {
  let hash = 7;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash || 7;
}

function clipShape(ctx: CanvasRenderingContext2D, size: number, shape: MedalShape): void {
  if (typeof Path2D === "undefined") return;
  ctx.setTransform(size / 100, 0, 0, size / 100, 0, 0);
  ctx.clip(new Path2D(SHAPES[shape]));
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

function drawRust(ctx: CanvasRenderingContext2D, w: number, cover: number, seed: number, shape: MedalShape, clear = true): void {
  ctx.globalCompositeOperation = "source-over";
  if (clear) ctx.clearRect(0, 0, w, w);
  if (cover <= 0) return;
  ctx.save();
  clipShape(ctx, w, shape);
  const r = rng(seed);
  const colors = ["#7a3210", "#a24a18", "#c8702e", "#5a2a10", "#d9964a", "#8c3c12"];
  ctx.fillStyle = `rgba(96,48,20,${(0.28 * cover).toFixed(3)})`;
  ctx.fillRect(0, 0, w, w);
  const n = Math.round(60 + 520 * cover * (w / 256));
  for (let i = 0; i < n; i++) {
    const x = r() * w, y = r() * w, rad = (3 + r() * 20) * (w / 256), c = colors[Math.floor(r() * colors.length)];
    const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
    g.addColorStop(0, c);
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.globalAlpha = (0.3 + r() * 0.6) * (0.4 + 0.6 * cover);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, rad, 0, 7);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  for (let j = 0; j < 420 * cover; j++) {
    ctx.fillStyle = r() > 0.5 ? "rgba(40,16,4,0.85)" : "rgba(230,150,70,0.8)";
    ctx.fillRect(r() * w, r() * w, 1 + r() * 2.2, 1 + r() * 2.2);
  }
  ctx.restore();
}

// grime: a dark, streaky film that only the sponge lifts
function drawGrime(ctx: CanvasRenderingContext2D, w: number, amt: number, seed: number, shape: MedalShape, clear = true): void {
  ctx.globalCompositeOperation = "source-over";
  if (clear) ctx.clearRect(0, 0, w, w);
  if (amt <= 0) return;
  const r = rng(seed * 7 + 3);
  ctx.save();
  clipShape(ctx, w, shape);
  ctx.fillStyle = `rgba(46,34,22,${(0.36 * amt).toFixed(3)})`;
  ctx.fillRect(0, 0, w, w);
  const n = Math.round(110 * amt * (w / 256));
  for (let i = 0; i < n; i++) {
    const x = r() * w, y = r() * w, rx = (8 + r() * 36) * (w / 256), ry = (2 + r() * 6) * (w / 256);
    ctx.globalAlpha = (0.18 + r() * 0.35) * amt;
    ctx.fillStyle = r() > 0.5 ? "#2a1f14" : "#53422e";
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, r() * 3.14, 0, 7);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

// haze: a pale, milky bloom that only the cloth wipes away
function drawHaze(ctx: CanvasRenderingContext2D, w: number, amt: number, seed: number, shape: MedalShape, clear = true): void {
  ctx.globalCompositeOperation = "source-over";
  if (clear) ctx.clearRect(0, 0, w, w);
  if (amt <= 0) return;
  const r = rng(seed * 11 + 5);
  ctx.save();
  clipShape(ctx, w, shape);
  ctx.fillStyle = `rgba(214,214,222,${(0.5 * amt).toFixed(3)})`;
  ctx.fillRect(0, 0, w, w);
  ctx.lineCap = "round";
  for (let i = 0; i < 16; i++) {
    ctx.strokeStyle = `rgba(255,255,255,${(0.22 * amt).toFixed(3)})`;
    ctx.lineWidth = (5 + r() * 12) * (w / 256);
    ctx.beginPath();
    ctx.arc(r() * w, r() * w, (20 + r() * 60) * (w / 256), r() * 6, r() * 6 + 2);
    ctx.stroke();
  }
  ctx.restore();
}

export type MedalStage = "rusty" | "grimy" | "dull" | "clean" | "mirror";
// the same thresholds the workbench tag uses
export const stageOf = (layers: MedalLayers): MedalStage => (layers.rust > 0.12 ? "rusty" : layers.grime > 0.12 ? "grimy" : layers.haze > 0.3 ? "dull" : layers.haze > 0.08 ? "clean" : "mirror");

export const dullOf = (layers: MedalLayers): number => Math.min(1, layers.rust * 0.8 + layers.grime * 0.45 + layers.haze * 0.3);

// ---------- the medal ----------

export interface MedalOpts {
  size?: number;
  layers?: MedalLayers;
  locked?: boolean;
  // the workbench size, with canvases fine enough to rub on
  big?: boolean;
}

export const BIG_CANVAS = 360;

export function medalEl(def: MedalDef, opts: MedalOpts = {}): HTMLElement {
  const size = opts.size || 88;
  const shape = medalShape(def);
  const metal = METALS[def.metal];
  const layers = opts.locked ? { rust: 0, grime: 0, haze: 0 } : opts.layers || { rust: 0, grime: 0, haze: 0 };
  const cw = opts.big ? BIG_CANVAS : size <= 32 ? 48 : 128;
  const el = document.createElement("div");
  el.className = `medal ${def.metal}${size <= 32 ? " tiny" : ""}${opts.locked ? " locked" : ""}`;
  el.style.setProperty("--s", `${size}px`);
  el.style.setProperty("--mask", maskUrl(shape));
  el.style.setProperty("--metal", metal.bg);
  el.style.setProperty("--ink-fill", metal.ink);
  el.innerHTML = `<div class="m-face"><div class="m-metal"></div><div class="m-bevel"></div>
    <div class="m-inlay"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="square" stroke-linejoin="miter"><path d="${iconPath(def.icon)}"/></svg></div>
    ${opts.big
      ? `<canvas class="m-haze" width="${cw}" height="${cw}"></canvas><canvas class="m-grime" width="${cw}" height="${cw}"></canvas><canvas class="m-rust" width="${cw}" height="${cw}"></canvas>
    <div class="m-wet"></div><div class="m-holo"></div><div class="m-sparkle"></div><div class="m-glare"></div>
    <canvas class="m-fx" width="${cw}" height="${cw}"></canvas>`
      : `<canvas class="m-flat" width="${cw}" height="${cw}"></canvas><div class="m-holo"></div>`}</div>
    <svg class="m-rim" viewBox="0 0 100 100" aria-hidden="true"><path d="${SHAPES[shape]}" stroke="${metal.rim}" stroke-width="2.2" stroke-opacity=".9"/></svg>`;
  paintLayers(el, layers, seedOf(def.id), shape, cw);
  return el;
}

// draws the three layers (again) and sets the dullness and the polished state
export function paintLayers(el: HTMLElement, layers: MedalLayers, seed: number, shape: MedalShape, cw: number): void {
  // the small medals on the shelves and lists keep all three layers on one canvas, hundreds of canvases make the case lag
  const flat = el.querySelector<HTMLCanvasElement>(".m-flat")?.getContext("2d");
  if (flat) {
    drawHaze(flat, cw, layers.haze, seed, shape);
    drawGrime(flat, cw, layers.grime, seed, shape, false);
    drawRust(flat, cw, layers.rust, seed, shape, false);
    el.style.setProperty("--dull", String(dullOf(layers)));
    el.classList.toggle("polished", !layers.rust && !layers.grime && !layers.haze && !el.classList.contains("locked"));
    el.dataset.stage = stageOf(layers);
    return;
  }
  const draw = (name: string, fn: typeof drawRust, amount: number) => {
    const ctx = el.querySelector<HTMLCanvasElement>(`.${name}`)?.getContext("2d");
    if (ctx) fn(ctx, cw, amount, seed, shape);
  };
  draw("m-haze", drawHaze, layers.haze);
  draw("m-grime", drawGrime, layers.grime);
  draw("m-rust", drawRust, layers.rust);
  el.style.setProperty("--dull", String(dullOf(layers)));
  el.classList.toggle("polished", !layers.rust && !layers.grime && !layers.haze && !el.classList.contains("locked"));
}
